/*
 * Pure logic for the Session allocation tab: IST formatting, grouping by start
 * time, counts, picker ordering and chips, and the copy the screen shows.
 * `now` is always a parameter so tests pin the clock.
 */

import { cityFromZone } from '../local_time';
import type { Tone } from '../ui';
import type { AllocRep, AllocSession, Allocation, FillResult } from './types';

export const IST_ZONE = 'Asia/Kolkata';
// IST has no DST, so shift maths can use a fixed offset.
const IST_OFFSET_MIN = 330;
const MINUTE_MS = 60 * 1000;
// The allocation tab refreshes this often while visible.
export const BOARD_POLL_MS = 60 * 1000;
// An unallocated session starting within this long gets a "starts soon" warning.
export const STARTS_SOON_MS = 60 * MINUTE_MS;

const toMs = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return v;
  if (!v) return NaN;
  return Date.parse(v);
};

// "7:20 PM"; ICU's narrow no-break space is folded to a plain space.
export const fmtTime = (
  at: string | number | null | undefined,
  zone: string = IST_ZONE
): string => {
  const t = toMs(at);
  if (Number.isNaN(t)) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
      .format(new Date(t))
      .replace(/\s/g, ' ');
  } catch {
    return '';
  }
};

// "Thu 29 Oct" in IST.
export const fmtDay = (at: string | number): string => {
  const t = toMs(at);
  if (Number.isNaN(t)) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_ZONE,
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  }).formatToParts(new Date(t));
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('weekday')} ${get('day')} ${get('month')}`;
};

// 'HH:MM' (24:00 allowed) to minutes of the day; null when malformed.
export const parseClock = (hhmm: string | null | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (min > 59 || h > 24 || (h === 24 && min > 0)) return null;
  return h * 60 + min;
};

// Shift clock 'HH:MM' as "9:00 PM".
export const fmtClock = (hhmm: string | null | undefined): string => {
  const total = parseClock(hhmm);
  if (total === null) return '';
  const h24 = Math.floor(total / 60) % 24;
  const min = total % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(min).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`;
};

export const istMinuteOfDay = (t: number): number =>
  (((Math.floor(t / MINUTE_MS) + IST_OFFSET_MIN) % 1440) + 1440) % 1440;

const DAY_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

type ShiftRep = Pick<AllocRep, 'shift_start' | 'shift_end' | 'weekly_off'>;

// goserver inShift: [start, end), end < start crosses midnight; returns the IST day code the
// shift started on, or null when `at` is outside the shift.
export const shiftStartDay = (rep: ShiftRep, at: string | number): string | null => {
  const start = parseClock(rep.shift_start);
  const end = parseClock(rep.shift_end);
  const t = toMs(at);
  if (start === null || end === null || Number.isNaN(t)) return null;
  const m = istMinuteOfDay(t);
  const istDay = new Date(t + IST_OFFSET_MIN * MINUTE_MS).getUTCDay();
  if (end >= start) return m >= start && m < end ? DAY_CODES[istDay] : null;
  if (m >= start) return DAY_CODES[istDay];
  if (m < end) return DAY_CODES[(istDay + 6) % 7];
  return null;
};

const isWeeklyOff = (rep: ShiftRep, day: string): boolean =>
  (rep.weekly_off || []).some((d) => String(d).toUpperCase() === day);

// On shift and not on the weekly off of the day the shift started (leave is on_leave_today).
export const inShiftAt = (rep: ShiftRep, at: string | number): boolean => {
  const day = shiftStartDay(rep, at);
  return !!day && !isWeeklyOff(rep, day);
};

// "New York 9:10 AM"; '' without a zone.
export const studentLocal = (tz: string | null | undefined, at: string | number): string => {
  if (!tz) return '';
  const time = fmtTime(at, tz);
  return time ? `${cityFromZone(tz)} ${time}` : '';
};

export const needsSomeone = (s: AllocSession): boolean => !s.allocation?.ops_uuid || !!s.cant_join;

const byStart = (a: AllocSession, b: AllocSession): number =>
  toMs(a.start_at) - toMs(b.start_at) ||
  (a.student_name || '').localeCompare(b.student_name || '') ||
  a.session_uuid.localeCompare(b.session_uuid);

export interface Chipish {
  label: string;
  tone: Tone;
}

export interface SessionGroup {
  key: string;
  running: boolean;
  title: string;
  need: number;
  chip: Chipish;
  join_window: string;
  sessions: AllocSession[];
}

// One group per distinct start instant, in start order.
export const groupSessions = (sessions: AllocSession[], now: number): SessionGroup[] => {
  const sorted = [...sessions].sort(byStart);
  const groups: SessionGroup[] = [];
  const byKey = new Map<number, AllocSession[]>();
  sorted.forEach((s) => {
    const k = toMs(s.start_at);
    const list = byKey.get(k);
    if (list) list.push(s);
    else byKey.set(k, [s]);
  });
  byKey.forEach((list, k) => {
    const running = k <= now;
    const n = list.length;
    const at = fmtTime(k);
    const need = list.filter(needsSomeone).length;
    const joinFrom = Math.min(...list.map((s) => toMs(s.join_at)));
    const joinTo = Math.max(...list.map((s) => toMs(s.end_at)));
    groups.push({
      key: String(k),
      running,
      title: `${running ? `Running now · started ${at}` : `Starting ${at}`} · ${
        n > 1 ? `${n} in parallel` : '1 session'
      }`,
      need,
      chip: need
        ? { label: `${need} need someone`, tone: 'error' }
        : { label: 'All covered', tone: 'success' },
      join_window: `${fmtTime(joinFrom)} to ${fmtTime(joinTo)}`,
      sessions: list
    });
  });
  return groups;
};

export interface BoardSummary {
  total: number;
  allocated: number;
  need: number;
  next_join: string;
}

export const summarize = (sessions: AllocSession[], now: number): BoardSummary => {
  const need = sessions.filter(needsSomeone).length;
  const upcoming = sessions
    .filter((s) => !needsSomeone(s) && toMs(s.end_at) > now)
    .sort((a, b) => toMs(a.join_at) - toMs(b.join_at) || byStart(a, b));
  const next = upcoming[0];
  let nextJoin = 'none';
  if (next) {
    const when = toMs(next.join_at) <= now ? 'now' : fmtTime(next.join_at);
    nextJoin = `${when} (${next.student_name}, ${next.allocation?.name || ''})`;
  }
  return { total: sessions.length, allocated: sessions.length - need, need, next_join: nextJoin };
};

// "Thu 29 Oct, 6:40 PM to 10:40 PM IST": the server's 4-hour window.
export const windowLine = (now: number): string =>
  `${fmtDay(now)}, ${fmtTime(now)} to ${fmtTime(now + 4 * 60 * MINUTE_MS)} IST`;

export const updatedLabel = (loadedAt: number, now: number): string => {
  if (!loadedAt) return '';
  const mins = Math.floor((now - loadedAt) / MINUTE_MS);
  if (mins < 1) return 'Updated just now';
  if (mins < 60) return `Updated ${mins} min ago`;
  return `Updated at ${fmtTime(loadedAt)}`;
};

export const liveStatus = (s: AllocSession, now: number): Chipish => {
  if (s.status === 'ACTIVE') {
    if (s.student_joined && s.teacher_joined)
      return { label: 'Live · both joined', tone: 'success' };
    if (s.student_joined) return { label: 'Live · student waiting', tone: 'warning' };
    if (s.teacher_joined) return { label: 'Live · teacher waiting', tone: 'warning' };
    return { label: 'Live', tone: 'success' };
  }
  if (toMs(s.start_at) <= now) return { label: 'Not started', tone: 'warning' };
  return { label: 'Scheduled', tone: 'neutral' };
};

export const allocatedByLine = (a: Allocation | null): string => {
  if (!a?.ops_uuid) return 'No one yet';
  const by =
    a.source === 'fill'
      ? `Filled by ${a.allocated_by_name || 'someone'}`
      : `Allocated by ${a.allocated_by_name || 'someone'}`;
  const at = fmtTime(a.allocated_at);
  return at ? `${by}, ${at}` : by;
};

// Server warnings first; an empty slot running or close to its start is flagged client-side.
export const rowWarnings = (s: AllocSession, now: number): string[] => {
  const out = (s.warnings || []).map((w) => w.text).filter(Boolean);
  const start = toMs(s.start_at);
  if (!s.allocation?.ops_uuid && start - now <= STARTS_SOON_MS) {
    out.push(
      start <= now ? 'Running and nobody is allocated' : 'Starts soon and nobody is allocated'
    );
  }
  return out;
};

export const cantJoinLabel = (s: AllocSession): string =>
  s.cant_join && s.allocation?.ops_uuid
    ? `${s.cant_join.name || 'The allocated rep'} can't join`
    : '';

export const levelLabel = (level: string | null | undefined): string =>
  level === 'L2' ? 'L2 backup' : level || '';

export type PickChip =
  'suggested' | 'free' | 'shift_ends' | 'said_cant_join' | 'busy' | 'off_shift';

export const PICK_CHIP: Record<PickChip, Chipish> = {
  suggested: { label: 'Suggested', tone: 'success' },
  free: { label: 'Free', tone: 'success' },
  shift_ends: { label: 'Shift ends', tone: 'warning' },
  said_cant_join: { label: "Can't join", tone: 'warning' },
  busy: { label: 'Busy', tone: 'error' },
  off_shift: { label: 'Off shift', tone: 'neutral' }
};

const CHIP_RANK: Record<PickChip, number> = {
  suggested: 0,
  free: 1,
  shift_ends: 2,
  said_cant_join: 3,
  busy: 4,
  off_shift: 5
};

export interface PickConfirm {
  title: string;
  body: string;
}

export interface PickOption {
  rep: AllocRep;
  chip: PickChip;
  line: string;
  selected: boolean;
  // Busy and off-shift picks ask first; the warning is still saved server-side.
  confirm: PickConfirm | null;
}

const CONFIRM_TAIL = ' If you continue, the warning is saved with the allocation.';

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const shiftText = (rep: AllocRep): string => {
  const from = fmtClock(rep.shift_start);
  const to = fmtClock(rep.shift_end);
  return from && to ? `${from} to ${to}` : '';
};

const levelRank = (level: string): number => (level === 'L3' ? 0 : 1);

const overlaps = (a: AllocSession, b: AllocSession): boolean =>
  toMs(a.join_at) < toMs(b.end_at) && toMs(b.join_at) < toMs(a.end_at);

// The server's list, plus the current rep's can't-join (an older server sends only that).
export const saidCantJoin = (s: AllocSession): Set<string> => {
  const out = new Set((s.cant_join_ops_uuids || []).filter(Boolean));
  if (s.cant_join?.ops_uuid) out.add(s.cant_join.ops_uuid);
  return out;
};

const optionFor = (rep: AllocRep, s: AllocSession, sessions: AllocSession[]): PickOption => {
  const mine = sessions.filter((o) => o.allocation?.ops_uuid === rep.ops_uuid);
  const clash = mine.find((o) => o.session_uuid !== s.session_uuid && overlaps(o, s));
  const join = fmtTime(s.join_at);
  const selected = s.allocation?.ops_uuid === rep.ops_uuid;
  const title = `Allocate ${rep.name} anyway?`;
  // Said Can't join outranks the rest: it is the rep's own answer for this session.
  if (saidCantJoin(s).has(rep.ops_uuid)) {
    return {
      rep,
      chip: 'said_cant_join',
      selected,
      line: "Said can't join",
      confirm: {
        title,
        body: `${rep.name} said they can't join this session.${CONFIRM_TAIL}`
      }
    };
  }
  if (clash) {
    return {
      rep,
      chip: 'busy',
      selected,
      line: `Joining ${clash.student_name} at ${fmtTime(clash.join_at)} (same window)`,
      confirm: {
        title,
        body: `${rep.name} is already joining ${clash.student_name} at ${fmtTime(clash.join_at)} to ${fmtTime(
          clash.end_at
        )} and can't be in both sessions' last 10 minutes.${CONFIRM_TAIL}`
      }
    };
  }
  const shift = shiftText(rep);
  let off = '';
  let offBody = '';
  if (rep.status && rep.status !== 'ACTIVE') {
    off = 'Not active on the sales team';
    offBody = `${rep.name} is not active on the sales team.`;
  } else if (rep.on_leave_today) {
    off = 'On leave today';
    offBody = `${rep.name} is on leave today.`;
  } else if (shiftStartDay(rep, s.join_at) && !inShiftAt(rep, s.join_at)) {
    const day = shiftStartDay(rep, s.join_at) || '';
    const label = `${day.charAt(0)}${day.slice(1).toLowerCase()}`;
    off = `Weekly off (${label})`;
    offBody = `${rep.name} has the weekly off on ${label}.`;
  } else if (!inShiftAt(rep, s.join_at)) {
    off = shift ? `Not on shift (shift ${shift})` : 'No shift set';
    offBody = `${rep.name} is not on shift at ${join}${shift ? ` (shift ${shift})` : ''}.`;
  }
  if (off) {
    return {
      rep,
      chip: 'off_shift',
      selected,
      line: off,
      confirm: { title, body: offBody + CONFIRM_TAIL }
    };
  }
  if (!inShiftAt(rep, toMs(s.end_at) - MINUTE_MS)) {
    return {
      rep,
      chip: 'shift_ends',
      selected,
      line: `Shift ends ${fmtClock(rep.shift_end)}, inside the join window`,
      confirm: null
    };
  }
  const others = mine.filter((o) => o.session_uuid !== s.session_uuid).length;
  return {
    rep,
    chip: 'free',
    selected,
    line: `Free at ${join} · on shift to ${fmtClock(rep.shift_end)} · ${plural(others, 'other session')}`,
    confirm: null
  };
};

// Byte order, like Go's string <, so ties break the way the server's planner does.
const byteCmp = (a: string, b: string): number => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

// Sessions in the plan already held by the rep, this one excluded (the fill planner's load).
const loadOf = (rep: AllocRep, s: AllocSession, sessions: AllocSession[]): number =>
  sessions.filter(
    (o) => o.session_uuid !== s.session_uuid && o.allocation?.ops_uuid === rep.ops_uuid
  ).length;

// goserver planFill's pick among reps on shift at join start with no overlap: L3 before L2,
// then fewest sessions in the plan, then name, then uuid.
export const fillChoice = (
  eligible: AllocRep[],
  s: AllocSession,
  sessions: AllocSession[]
): AllocRep | null =>
  [...eligible].sort(
    (a, b) =>
      levelRank(a.level) - levelRank(b.level) ||
      loadOf(a, s, sessions) - loadOf(b, s, sessions) ||
      byteCmp(a.name || '', b.name || '') ||
      byteCmp(a.ops_uuid, b.ops_uuid)
  )[0] || null;

// Suggested (what Fill would pick) first, then Free, Shift ends, Busy, Off shift; L3 before L2, then name.
export const pickerOptions = (
  s: AllocSession,
  reps: AllocRep[],
  sessions: AllocSession[]
): PickOption[] => {
  const opts = reps.map((r) => optionFor(r, s, sessions));
  // Fill skips busy and off-shift reps (a shift ending inside the window still counts) and,
  // on a can't-join row, re-picks without the rep who can't join.
  const pick = fillChoice(
    opts.filter((o) => o.chip === 'free' || o.chip === 'shift_ends').map((o) => o.rep),
    s,
    sessions
  );
  const suggested = opts.find((o) => o.rep === pick);
  if (suggested) suggested.chip = 'suggested';
  return opts.sort(
    (a, b) =>
      CHIP_RANK[a.chip] - CHIP_RANK[b.chip] ||
      levelRank(a.rep.level) - levelRank(b.rep.level) ||
      (a.rep.name || '').localeCompare(b.rep.name || '') ||
      a.rep.ops_uuid.localeCompare(b.rep.ops_uuid)
  );
};

export interface RepLoad {
  rep: AllocRep;
  level: string;
  shift: string;
  line: string;
}

// Right panel: reps on shift now or holding an allocation in the window.
export const repLoads = (reps: AllocRep[], sessions: AllocSession[]): RepLoad[] =>
  reps
    .map((rep) => {
      const mine = sessions
        .filter((s) => s.allocation?.ops_uuid === rep.ops_uuid)
        .sort((a, b) => toMs(a.join_at) - toMs(b.join_at));
      return { rep, mine };
    })
    .filter(({ rep, mine }) => rep.on_shift_now || mine.length > 0)
    .sort(
      (a, b) =>
        levelRank(a.rep.level) - levelRank(b.rep.level) ||
        (a.rep.name || '').localeCompare(b.rep.name || '')
    )
    .map(({ rep, mine }) => ({
      rep,
      level: levelLabel(rep.level),
      shift: shiftText(rep) || 'No shift set',
      line: mine.length
        ? `${plural(mine.length, 'session')} · joins ${mine.map((s) => fmtTime(s.join_at)).join(', ')}`
        : '0 sessions · free'
    }));

// The toast for a 409: someone saved first.
export const staleMessage = (student: string, current: Allocation | null | undefined): string => {
  if (current?.ops_uuid) {
    return `${current.allocated_by_name || 'Someone'} just allocated ${student} to ${current.name}`;
  }
  return `Someone just changed ${student}; reloaded`;
};

export const savedMessage = (s: AllocSession, repName: string): string =>
  repName
    ? `${repName} will join ${s.student_name} at ${fmtTime(s.join_at)}`
    : `${s.student_name} is not allocated now`;

export const fillMessage = (r: FillResult): string => {
  const filled = r.filled.length;
  const skipped = r.skipped.length;
  const head = filled ? `Filled ${plural(filled, 'session')}` : 'No empty slots were filled';
  return skipped ? `${head} · ${skipped} had no free rep` : head;
};

export type ListboxAction =
  { kind: 'move'; index: number } | { kind: 'choose' } | { kind: 'close' };

// Listbox keys: arrows/Home/End move the active option, Enter/Space choose, Escape closes.
export const listboxKey = (key: string, active: number, count: number): ListboxAction | null => {
  const last = Math.max(count - 1, 0);
  if (key === 'ArrowDown') return { kind: 'move', index: Math.min(active + 1, last) };
  if (key === 'ArrowUp') return { kind: 'move', index: Math.max(active - 1, 0) };
  if (key === 'Home') return { kind: 'move', index: 0 };
  if (key === 'End') return { kind: 'move', index: last };
  if (key === 'Enter' || key === ' ') return { kind: 'choose' };
  if (key === 'Escape') return { kind: 'close' };
  // Tab is left to the browser: focus moves on and the listbox closes on blur.
  return null;
};

export type PickerChoice = { kind: 'clear' } | { kind: 'pick'; option: PickOption } | null;

// Row 0 clears; an index past a list a poll just shrank chooses nothing rather than clearing.
export const pickerChoice = (index: number, options: PickOption[]): PickerChoice => {
  if (index === 0) return { kind: 'clear' };
  const option = options[index - 1];
  return option ? { kind: 'pick', option } : null;
};

// The active row after the options change: same rep if still listed, else clamped.
export const clampActive = (
  active: number,
  prevUuid: string | null,
  options: PickOption[]
): number => {
  if (prevUuid) {
    const i = options.findIndex((o) => o.rep.ops_uuid === prevUuid);
    if (i >= 0) return i + 1;
  }
  return Math.min(Math.max(active, 0), options.length);
};
